import { cloneDeep } from 'lodash'
import {
  AnswerSchema,
  BotFlow,
  CarouselButtonSchema,
  CarouselSchema,
  DialogSchema,
  BotMutationDefinition,
  MutationRule,
  StepSchema
} from '@feedyou/schema'

import { StepData } from './types'
import { confirm } from '../bootbox'
import { MutationItem } from '../types'
import { sanitize } from '../sanitizeText'
import { mutateItem } from '../mutation'
import { HeaderMainKeys, MUTATION_ID_SEPARATOR } from './constants'
import { getMutationCombinations } from './getMutationCombinations'
import { extractStepsFromDialogs } from '../dialogs/extractStepsFromDialogs'
import { getHeaderMainKeyIndexes } from './convertService'
import { getSubStepTypeIds } from '../getSubStepTypes'

export async function convertDialogsToXLS(
  flow: BotFlow,
  dialogsToExport: DialogSchema[]
): Promise<string[][]> {
  const header = getHeader()
  const rows: string[][] = [header]

  const canExport = await canExportEveryStep(dialogsToExport)

  if (!canExport) {
    return []
  }

  setRows(dialogsToExport, rows)

  if (rows.length <= 1) {
    return rows
  }

  return addBotMutation(rows, dialogsToExport, flow.mutations)
}

function getHeader(): string[] {
  return [
    HeaderMainKeys.DIALOG_ID,
    HeaderMainKeys.STEP_ID,
    HeaderMainKeys.SUB_STEP_TYPE,
    HeaderMainKeys.SUB_STEP_ID,
    HeaderMainKeys.STEP_TYPE,
    HeaderMainKeys.STEP_VALUE
  ]
}

function setRows(dialogs: DialogSchema[], rows: string[][]) {
  dialogs.forEach(dialog => {
    dialog.steps.forEach(step => {
      switch (step.type) {
        case 'message':
          addMessageRow(rows, step, dialog)
          break
        case 'question':
          addQuestionRow(rows, step, dialog)
          break
        case 'email':
          pushRows(rows, dialog, step, ['subject', 'bodyPrefix', 'bodyPostfix', 'body'])
          break
        case 'prompt':
          pushRows(rows, dialog, step, ['prompt', 'promptMale', 'promptFemale'])
          break

        default:
      }
    })
  })
}

function addMessageRow(rows: string[][], step: StepSchema, dialog: DialogSchema) {
  pushRows(rows, dialog, step, ['message', 'messageMale', 'messageFemale'])

  if (!step.carousel) {
    return
  }

  step.carousel.forEach((item: CarouselSchema) => {
    pushRows(rows, dialog, step, ['title', 'subtitle', 'text'], 'carousel', item)

    item.buttons &&
      item.buttons.forEach((button: CarouselButtonSchema) => {
        pushRows(rows, dialog, step, ['title'], 'carousel.button', button)
      })
  })
}

function addQuestionRow(rows: string[][], step: StepSchema, dialog: DialogSchema) {
  pushRows(rows, dialog, step, [
    'question',
    'questionMale',
    'questionFemale',
    'skipAnswer',
    'skipFirstAnswer',
    'questionFirst',
    'otherAnswer'
  ])

  if (!step.answers) {
    return
  }

  step.answers.forEach((answer: AnswerSchema) => {
    pushRows(
      rows,
      dialog,
      step,
      ['answer', 'title', 'subtitle', 'text', 'button'],
      'answer',
      answer
    )
  })
}

function pushRows(
  rows: string[][],
  dialog: DialogSchema,
  step: StepSchema,
  keys: string[],
  title?: string,
  subitem?: MutationItem
) {
  const item: { [k: string]: any } = mutateItem(subitem || step)

  keys.forEach(key => {
    const value = item[key]

    if (!value) {
      return
    }

    rows.push([
      dialog.id,
      step.id,
      title || '',
      subitem ? subitem.id || '' : '',
      key,
      sanitize(value)
    ])
  })
}

export function addBotMutation(
  rows: string[][],
  dialogs: DialogSchema[],
  mutations?: BotMutationDefinition[]
) {
  if (!mutations?.length) {
    return rows
  }

  const mutationCombinations = getMutationCombinations(mutations)
  const rowsWithMutations = getRowsWithMutations(rows, dialogs, mutationCombinations)

  rowsWithMutations[0] = [...rowsWithMutations[0], ...mutationCombinations]

  return rowsWithMutations
}

function getRowsWithMutations(
  rows: string[][],
  dialogs: DialogSchema[],
  mutationCombinations: string[]
) {
  const header = getHeader()
  const rowsCopy = cloneDeep(rows)

  const { stepIdIndex, stepTypeIndex, dialogIdIndex, subStepIdIndex, subStepTypeIndex } =
    getHeaderMainKeyIndexes(header)

  rows.forEach((row, index) => {
    const stepId = row[stepIdIndex]
    const dialogId = row[dialogIdIndex]
    const stepType = row[stepTypeIndex]
    const stepItemId = row[subStepIdIndex]
    const stepItemType = row[subStepTypeIndex]

    for (const { id, steps } of dialogs) {
      const foundStep = id === dialogId && steps?.find((step: StepSchema) => step.id === stepId)

      if (!foundStep) {
        continue
      }

      const foundStepItem = getStepItem(foundStep, stepItemId, stepItemType)

      if (!foundStep.mutations && !foundStepItem?.mutations) {
        continue
      }

      const mutationCells = getMutationCells(
        stepType,
        foundStepItem || foundStep,
        mutationCombinations
      )

      if (mutationCells.length) {
        rowsCopy[index].push(...mutationCells)
      }
    }
  })

  return rowsCopy
}

function getStepItem(
  foundStep: { [k: string]: StepSchema },
  stepItemId: string,
  stepItemType: string
) {
  if (!stepItemType) {
    return undefined
  }

  const subStepTypeIds = getSubStepTypeIds(stepItemType)

  if (subStepTypeIds.length === 1) {
    const stepItemType = subStepTypeIds[0]

    return foundStep[stepItemType].find(
      (stepItem: CarouselButtonSchema | AnswerSchema) => stepItem.id === stepItemId
    )
  }

  if (subStepTypeIds.length === 2) {
    const [carousel, buttons] = subStepTypeIds

    for (let carouselIndex = 0; carouselIndex < foundStep[carousel].length; carouselIndex++) {
      const foundButton = foundStep?.[carousel][carouselIndex]?.[buttons]?.find(
        (button: CarouselButtonSchema) => button.id === stepItemId
      )

      if (foundButton) {
        return foundButton
      }
    }
  }

  return undefined
}

function getMutationCells(
  stepType: string,
  foundStep: StepSchema,
  mutationCombinations: string[]
): string[] {
  const mutationCells = []
  mutationCells.push(...mutationCombinations.map(() => ''))

  mutationCombinations?.forEach((mutationCombination, index) => {
    const mutationIds = mutationCombination.split(MUTATION_ID_SEPARATOR)

    const mutations = foundStep?.mutations?.filter((mutation: MutationRule) =>
      mutation?.set?.find(setItem => setItem.field === stepType)
    )

    mutations?.forEach((mutation: MutationRule) => {
      if (!mutation) {
        return
      }

      if (!mutation.test) {
        return
      }

      if (mutation.test.length !== mutationIds.length) {
        return
      }

      const foundMutations = mutation.test.filter(
        testItem => testItem.value && mutationIds.includes(testItem.value)
      )

      if (mutationIds.length !== foundMutations.length) {
        return
      }

      if (!mutation.set) {
        return
      }

      const foundSetItem = mutation.set.find(setItem => setItem.field === stepType)

      mutationCells[index] = getSanizedValue(foundSetItem?.value)
    })
  })

  return mutationCells
}

function getSanizedValue(value?: string): string | undefined {
  if (!value) {
    return
  }

  return sanitize(value)
}

function hasStepMulitpleSets(mutations?: MutationRule[]): boolean {
  if (!mutations) {
    return false
  }

  return mutations?.some(mutation => mutation?.set?.length && mutation?.set?.length > 1)
}

async function canExportEveryStep(dialogs: DialogSchema[]): Promise<boolean> {
  const stepsToConfirm: StepData[] = []

  const allSteps = extractStepsFromDialogs(dialogs)
  const supportedSteps = allSteps.filter(step => step?.type && isSupportedStep(step.type))

  supportedSteps.forEach(step => {
    const stepsWithMulitpleSets = getStepsWithMulitpleSets(step)
    stepsToConfirm.push(...stepsWithMulitpleSets)
  })

  return await getConfimDialog(stepsToConfirm)
}

export function isSupportedStep(type: string): boolean {
  if (type === 'email') {
    return true
  }

  if (type === 'prompt') {
    return true
  }

  if (type === 'message') {
    return true
  }

  if (type === 'question') {
    return true
  }

  return false
}

function getStepsWithMulitpleSets(step: StepSchema): StepData[] {
  const { id, type = '' } = step
  const steps: StepData[] = []

  if (hasStepMulitpleSets(step?.mutations)) {
    steps.push({ id, type })
  }

  if (type === 'message') {
    const subSteps = getCarouselItemsWithMulitpleSets(step)
    steps.push(...subSteps)
  }

  if (type === 'question') {
    const subSteps = getAnswersWithMulitpleSets(step)
    steps.push(...subSteps)
  }

  return steps
}

function getAnswersWithMulitpleSets(step: StepSchema) {
  if (!step.answers) {
    return []
  }

  return step.answers.reduce((output: StepData[], answer: AnswerSchema) => {
    if (!hasStepMulitpleSets(answer?.mutations)) {
      return output
    }

    output.push({ id: answer.id || '', type: 'question answer' })
    return output
  }, [])
}

function getCarouselItemsWithMulitpleSets(step: StepSchema): StepData[] {
  if (!step.carousel) {
    return []
  }

  return step.carousel.reduce((output: StepData[], carouselItem: CarouselSchema) => {
    if (hasStepMulitpleSets(carouselItem?.mutations)) {
      output.push({ id: carouselItem.id || '', type: 'carousel' })
    }

    const buttons = getCarouselItemButtonsWithMulitpleSets(carouselItem)
    output.push(...buttons)

    return output
  }, [])
}

function getCarouselItemButtonsWithMulitpleSets(step: StepSchema | CarouselSchema): StepData[] {
  if (!step.buttons) {
    return []
  }

  return step.buttons.reduce((output: StepData[], button: CarouselButtonSchema) => {
    if (!hasStepMulitpleSets(button?.mutations)) {
      return output
    }

    output.push({ id: button.id || '', type: 'carousel button' })
    return output
  }, [])
}

async function getConfimDialog(steps: StepData[]): Promise<boolean> {
  if (!steps.length) {
    return true
  }

  const stepInfoList = getStepInfoList(steps)

  const title = 'Do you really want to export texts?'
  const message = `There is multiple set values in mutation of:<br />${stepInfoList}<br />Import texts may not be successful!<br />Should continue with export?`

  return await confirm({ title, message })
}

function getStepInfoList(steps: StepData[]): string {
  return steps.reduce((output: string, step: StepData) => {
    const { id, type } = step

    return `${output}${type} step with id: "${id}"<br />`
  }, '')
}
